import React, { useState, useEffect, useRef } from 'react';
import ReportSoalButton from '../LanguageLearning/ReportSoalButton';
import PracticeCloseButton from '../Practice/PracticeCloseButton';
import DarkModeToggle from '../DarkModeToggle';
import Speaking from './Speaking';
import Prononciation from './Prononciation';
// changed by Cursor: detect webview for back button positioning
const isWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (localStorage.getItem('isWebView') === 'true') {
    return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webview') === 'true') {
    return true;
  }
  if (window.ReactNativeWebView) {
    return true;
  }
  if (window.__WEBVIEW__ || window.isWebView) {
    return true;
  }
  if (window.webkit && window.webkit.messageHandlers && Object.keys(window.webkit.messageHandlers).length > 0) {
    return true;
  }
  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();
  if (userAgentLower.includes('wv') && !userAgentLower.includes('chrome')) {
    return true;
  }
  return false;
};

const SpeakingPractice = ({ questions = [], onComplete, onClose, hideCompletedScreen = false, sessionData = null, langCode = 'de', practiceTitle = 'Speaking Practice' }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const hasCalledOnComplete = useRef(false);
  const questionStartTimeRef = useRef(null); // Track when current question started

  const currentQuestion = questions[currentQuestionIndex];
  
  // Generate storage key based on session-id
  const storageKey = sessionData?.['session-id'] 
    ? `speaking-practice-answers-${sessionData['session-id']}`
    : null;

  // Track previous storageKey to detect session changes
  const prevStorageKeyRef = useRef(null);
  
  // Reset answers when questions change (new practice session)
  // This should run BEFORE loading from localStorage to ensure clean state
  useEffect(() => {
    const isNewSession = prevStorageKeyRef.current !== storageKey;
    
    if (isNewSession) {
      // Clear answers immediately for new session
      setAnswers([]);
      setCurrentQuestionIndex(0);
      hasCalledOnComplete.current = false;
      questionStartTimeRef.current = null;
      
      // Update ref
      prevStorageKeyRef.current = storageKey;
    } else if (questions.length === 0) {
      // Questions cleared, reset state
      setAnswers([]);
      setCurrentQuestionIndex(0);
      hasCalledOnComplete.current = false;
      questionStartTimeRef.current = null;
    }
  }, [questions.length, storageKey]);

  // Track question start time when question changes
  useEffect(() => {
    if (currentQuestion) {
      questionStartTimeRef.current = Date.now();
    }
  }, [currentQuestionIndex, currentQuestion]);

  // changed by Cursor: auto scroll ke atas setiap ganti soal (terutama mobile)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentQuestionIndex]);

  // Load answers + currentIndex from localStorage on mount (same session only)
  useEffect(() => {
    if (questions.length === 0) return;
    if (prevStorageKeyRef.current !== storageKey && prevStorageKeyRef.current !== null) return;

    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          // Support both old format (plain array) and new format ({ answers, currentIndex })
          const answersArr = Array.isArray(parsed) ? parsed : (parsed.answers || []);
          const savedIndex = Array.isArray(parsed) ? null : (parsed.currentIndex ?? null);

          if (answersArr.length > 0) {
            // Accept partial saves - just ensure answers belong to current questions
            const questionIds = new Set(questions.map(q => q['soal-id'] || q.id));
            const valid = answersArr.every(a => !a || questionIds.has(a.questionId || a['question-id'] || a['soal-id']));
            if (valid) {
              setAnswers(answersArr);
              if (savedIndex !== null) {
                setCurrentQuestionIndex(Math.min(savedIndex, questions.length - 1));
              } else {
                const answered = answersArr.filter(Boolean).length;
                setCurrentQuestionIndex(Math.min(answered, questions.length - 1));
              }
            } else {
              localStorage.removeItem(storageKey);
              setAnswers([]);
            }
          }
        }
      } catch (_) {
        setAnswers([]);
      }
    } else {
      setAnswers([]);
    }
  }, [storageKey, questions]);

  // Save answers + currentIndex to localStorage; strip audio Blobs (not serializable)
  useEffect(() => {
    if (!storageKey || answers.filter(Boolean).length === 0) return;
    try {
      const toSave = answers.map(a => {
        if (!a) return null;
        const { answer, ...rest } = a;
        const isBlob = (typeof Blob !== 'undefined' && answer instanceof Blob) ||
                       (typeof File !== 'undefined' && answer instanceof File);
        return { ...rest, answer: isBlob ? null : answer };
      });
      localStorage.setItem(storageKey, JSON.stringify({ answers: toSave, currentIndex: currentQuestionIndex }));
    } catch (_) {}
  }, [answers, currentQuestionIndex, storageKey]);

  const handleAnswer = (answerData) => {
    // Calculate duration for current question (in milliseconds for speaking practice)
    let duration = 0;
    if (questionStartTimeRef.current) {
      const endTime = Date.now();
      const durationMs = endTime - questionStartTimeRef.current;
      duration = durationMs; // Keep in milliseconds for speaking practice
    }
    
    // Add duration to answerData
    const answerWithDuration = {
      ...answerData,
      duration: duration,
    };
    
    setAnswers(prevAnswers => {
      // Check if answer already exists for this question (update instead of add)
      const existingIndex = prevAnswers.findIndex(
        a => (a.questionId || a['question-id'] || a['soal-id']) === (answerData.questionId || answerData['question-id'] || answerData['soal-id'])
      );
      
      if (existingIndex >= 0) {
        // Update existing answer
        const newAnswers = [...prevAnswers];
        newAnswers[existingIndex] = answerWithDuration;
        console.log(`✅ Updated answer at index ${existingIndex} for question ${currentQuestionIndex + 1}`);
        return newAnswers;
      } else {
        // Add new answer - ensure it's at the correct index position
        // This ensures answers array matches questions array order
        const newAnswers = [...prevAnswers];
        
        // If current index is beyond array length, fill with undefined/null
        // Changed by Cursor: Add safety checks to prevent infinite loop
        const MAX_ARRAY_SIZE = 10000; // Safety limit
        while (newAnswers.length <= currentQuestionIndex && 
               Number.isFinite(currentQuestionIndex) && 
               currentQuestionIndex >= 0 && 
               currentQuestionIndex < MAX_ARRAY_SIZE) {
          newAnswers.push(null);
        }
        
        // Set answer at current question index
        newAnswers[currentQuestionIndex] = answerWithDuration;
        return newAnswers;
      }
    });
    
    // Reset start time for next question
    questionStartTimeRef.current = null;
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Reset start time for next question
      questionStartTimeRef.current = null;
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleSubmit = () => {
    // Last question - submit all answers
    if (onComplete && !hasCalledOnComplete.current) {
      hasCalledOnComplete.current = true;
      onComplete(answers);
    }
  };

  // Map backend question type to component type
  const mapQuestionType = (backendType) => {
    const typeMap = {
      'speaking': 'speaking',
      'pronunciation': 'pronunciation',
      'pronun': 'pronunciation',
    };
    return typeMap[backendType] || 'speaking';
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    const questionType = mapQuestionType(currentQuestion.type || currentQuestion['question-type'] || 'speaking');
    const questionId = currentQuestion['soal-id'] || currentQuestion.id || currentQuestionIndex;
    
    // Check if it's pronunciation type
    const isPronunciation = questionType === 'pronunciation' || 
                           currentQuestion['subpractice-type'] === 'pronunciation' ||
                           currentQuestion.type?.toLowerCase().includes('pronun');
    
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    
    const commonProps = {
      question: { ...currentQuestion, number: currentQuestionIndex + 1 },
      onAnswer: handleAnswer,
      onNext: isLastQuestion ? handleSubmit : handleNext,
      isLastQuestion: isLastQuestion,
      sessionData: sessionData,
      langCode: langCode
    };

    // Use key prop to force re-mount when question changes, ensuring state is reset
    if (isPronunciation) {
      return <Prononciation key={questionId} {...commonProps} />;
    }
    
    return <Speaking key={questionId} {...commonProps} />;
  };


  // changed by Cursor: detect webview for back button positioning
  const inWebView = isWebView();

  const prosetId = currentQuestion?.['proset-id'] || currentQuestion?.prosetId || currentQuestion?.proset_id || null;
  const soalId = currentQuestion?.['soal-id'] || currentQuestion?.soalId || currentQuestion?.id || null;

  return (
    <div className="w-full max-w-[1560px] md:max-w-[1061px] mx-auto p-6 md:p-4 md:px-5 bg-[var(--card-bg)] rounded-2xl shadow-[var(--shadow-sm)] flex flex-col relative max-md:max-w-none max-md:p-4 max-md:px-3 max-md:pb-[140px] max-md:rounded-none max-md:shadow-none max-md:min-h-[100dvh]">
      {/* Back left, Title center, Report right - same row */}
      <div className="relative flex items-center justify-center w-full mb-4 gap-2 min-h-[44px] [&_.practice-close-button]:mb-0">
        <div className="absolute left-0 shrink-0 z-10">
          {onClose ? (
            <PracticeCloseButton onClose={onClose} inWebView={inWebView} />
          ) : (
            <span />
          )}
        </div>
        <h2 className="m-0 text-lg font-semibold text-[var(--text-primary)] text-center leading-tight max-md:text-xs max-md:max-w-[calc(100%-120px)] max-md:truncate">
          {practiceTitle}
        </h2>
        <div className="absolute right-0 flex items-center gap-2 shrink-0 z-10">
          <DarkModeToggle variant="practice" />
          <ReportSoalButton type="speaking" prosetId={prosetId} soalId={soalId} allowReReport={false} />
        </div>
      </div>
      <div className="mb-6 max-md:mb-5 md:mb-4">
        <div className="flex justify-between mb-2 text-sm font-semibold text-[var(--text-primary)] max-md:text-[13px]">
          <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
        </div>
        <div className="w-full h-2 bg-[rgba(18,118,109,0.1)] dark:bg-[rgba(148,163,184,0.15)] rounded-full overflow-hidden max-md:h-1.5">
          <div
            className="h-full bg-gradient-to-r from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#14b8a6] dark:via-[#0d9488] dark:to-[#0f766e] rounded-full transition-[width] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>
      
      {renderQuestion()}
    </div>
  );
};

export default SpeakingPractice;

// Export individual question components for reuse in review page
export {
  Speaking,
  Prononciation
};

